import type { IntlFormatters } from 'react-intl';
import { defineMessages } from 'react-intl';

import { BLOCK_ACTIONS_TEMPLATE_SECTION } from '@atlaskit/editor-common/block-menu';
import type {
	BlockMenuExtensionConfiguration,
	ExtensionConfiguration,
	ExtensionMenuItemConfiguration,
	ToolbarExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import SaveIcon from '@atlaskit/icon-lab/core/save';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import SearchIcon from '@atlaskit/icon/core/search';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { ANALYTICS_ORIGIN, type AnalyticsOrigin } from '../constants/snippetAnalytics.ts';

/**
 * Options for building the snippet extension configuration.
 */
type BuildSnippetExtensionOptions = {
	/** Intl formatMessage function for i18n */
	formatMessage: IntlFormatters['formatMessage'];
	/** Handler called when user clicks create snippet. Receives the origin ('contextualToolbar' | 'blockMenu'). */
	onCreateSnippet: (origin: AnalyticsOrigin) => void;
	/** Handler called when user clicks browse templates in block menu. */
	onOpenTemplatePanel: () => void;
};

const messages = defineMessages({
	blockTemplates: {
		id: 'snippets.blockTemplates',
		defaultMessage: 'Block templates',
		description: 'Label for the block templates submenu in the block menu',
	},
	createBlockTemplate: {
		id: 'snippets.createBlockTemplate',
		defaultMessage: 'Create block template',
		description: 'Label for the create block template button in the contextual toolbar',
	},
	browseTemplates: {
		id: 'snippets.browseTemplates',
		defaultMessage: 'Browse templates',
		description: 'Label for the browse templates action in the block templates submenu',
	},
	createBlockTemplateMenuItem: {
		id: 'snippets.createBlockTemplateMenuItem',
		defaultMessage: 'Save block template',
		description: 'Label for the save block template action in the block templates submenu',
	},
	browseTemplatesMenuItem: {
		id: 'snippets.browseTemplatesMenuItem',
		defaultMessage: 'Browse all templates',
		description: 'Label for the browse all templates action in the block templates submenu',
	},
	createBlockTemplateLozenge: {
		id: 'snippets.createBlockTemplateLozenge',
		defaultMessage: 'New',
		description: 'Lozenge label for the create block template button in the contextual toolbar',
	},
});

/**
 * Builds the snippet extension configuration for the selection extension.
 *
 * Pure function that returns the menu items for the selection extension
 * toolbar and block menu. The actual creation logic (getting selection,
 * analytics, showing modal) is provided by the consumer via the
 * `onCreateSnippet` handler.
 *
 * @returns Extension configuration array (empty if feature is disabled)
 */
export const buildSnippetExtension = ({
	formatMessage,
	onCreateSnippet,
	onOpenTemplatePanel,
}: BuildSnippetExtensionOptions): ExtensionConfiguration[] => {
	if (!expVal('cc_snippets', 'isEnabled', false)) {
		return [];
	}

	const toolbarConfiguration: ToolbarExtensionConfiguration = {
		getMenuItems: () => {
			return [
				{
					label: formatMessage(messages.createBlockTemplate),
					lozenge: {
						label: formatMessage(messages.createBlockTemplateLozenge),
					},
					icon: TemplateIcon,
					onClick: () => onCreateSnippet(ANALYTICS_ORIGIN.CONTEXTUAL_TOOLBAR),
				},
			];
		},
	};

	/**
	 * placement: 'featured' allows the button to show up on the top level of the
	 * block menu, instead of under the Turn Into -> Create subsection
	 */
	const blockMenuConfiguration: BlockMenuExtensionConfiguration = {
		...(fg('platform_editor_block_menu_v2_patch_2')
			? {
					placement: 'featured-section' as const,
					sectionKey: BLOCK_ACTIONS_TEMPLATE_SECTION.key,
				}
			: { placement: 'featured' as const }),
		getMenuItems: (): ExtensionMenuItemConfiguration[] =>
			fg('platform_editor_block_menu_v2_patch_2')
				? [
						{
							key: 'block-templates',
							label: formatMessage(messages.blockTemplates),
							icon: TemplateIcon,
							lozenge: {
								label: formatMessage(messages.createBlockTemplateLozenge),
							},
							getMenuItems: () => [
								{
									key: 'create-block-template',
									label: formatMessage(messages.createBlockTemplateMenuItem),
									icon: SaveIcon,
									onClick: () => onCreateSnippet(ANALYTICS_ORIGIN.BLOCK_MENU),
								},
								{
									key: 'browse-templates',
									label: formatMessage(messages.browseTemplatesMenuItem),
									icon: SearchIcon,
									onClick: onOpenTemplatePanel,
								},
							],
						},
					]
				: [
						{
							key: 'block-templates',
							label: formatMessage(messages.blockTemplates),
							icon: TemplateIcon,
							getMenuItems: () => [
								{
									key: 'create-block-template',
									label: formatMessage(messages.createBlockTemplate),
									icon: TemplateIcon,
									onClick: () => onCreateSnippet(ANALYTICS_ORIGIN.BLOCK_MENU),
								},
								{
									key: 'browse-templates',
									label: formatMessage(messages.browseTemplates),
									icon: PanelRightIcon,
									onClick: onOpenTemplatePanel,
								},
							],
						},
					],
	};

	return [
		{
			key: 'create-snippet',
			source: 'first-party',
			inlineToolbar: toolbarConfiguration,
			blockMenu: blockMenuConfiguration,
		},
	];
};
